import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import Icon from '../../../../shared/_TBComponents/Icon';
import { calendarIcons } from '../../../../shared/constants';

// Read-only row mirroring the calendar's type-colored icon bubbles (via the
// shared calendarIcons map) + grouping badge, but with no click/dispatch behavior.

// The detail payload's `icon` enum doesn't map 1:1 to the calendarIcons keys —
// translate so every row resolves to a real entry instead of the fallback.
const CALENDAR_ICON_KEY = {
  warmup: 'warm-up',
  cooldown: 'cool-down',
  rest: 'resttime',
  chart: 'progression',
  'question-mark': 'questionnaire',
};

const FALLBACK_TYPE = {
  icon: 'lift',
  iconColor: '#6C7A89',
  containerColor: 'rgba(108, 122, 137, 0.2)',
};

const Row = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
  flex-shrink: 0;
`;

const IconCircle = styled.div`
  position: relative;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 25px;
  height: 25px;
  border-radius: 24px;
  border: 1px solid ${(props) => props.borderColor};
  background: ${(props) => props.bgColor};
`;

const GroupingBadge = styled.div`
  position: absolute;
  right: -3px;
  bottom: -3px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 1px solid ${(props) => props.color};
  background: ${(props) => props.color};
  color: #FFF;
  font-size: 8px;
  font-weight: 700;
  line-height: 1;
`;

const TitleCol = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const ExName = styled.span`
  font-family: "Nunito Sans";
  font-size: 12px;
  font-weight: 700;
  line-height: 1.5;
  color: #1A1A1A;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ExSub = styled.span`
  font-family: "Nunito Sans";
  font-size: 12px;
  font-weight: 400;
  line-height: 1.5;
  color: #1A1A1A;
  white-space: pre-line;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ExercisePreviewRow = ({ exercise }) => {
  const {
    title, subTitle, icon, groupingLetter, groupingColorCode,
  } = exercise;
  const typeStyle = calendarIcons[CALENDAR_ICON_KEY[icon] || icon] || FALLBACK_TYPE;

  return (
    <Row>
      <IconCircle borderColor={typeStyle.iconColor} bgColor={typeStyle.containerColor}>
        <Icon icon={typeStyle.icon} size={16} color={typeStyle.iconColor} />
        {groupingLetter && (
          <GroupingBadge color={groupingColorCode || '#9E9E9E'}>{groupingLetter}</GroupingBadge>
        )}
      </IconCircle>
      <TitleCol>
        <ExName>{title || 'Exercise'}</ExName>
        {subTitle && <ExSub>{subTitle}</ExSub>}
      </TitleCol>
    </Row>
  );
};

ExercisePreviewRow.propTypes = {
  exercise: PropTypes.shape({
    title: PropTypes.string,
    subTitle: PropTypes.string,
    icon: PropTypes.string,
    groupingLetter: PropTypes.string,
    groupingColorCode: PropTypes.string,
  }).isRequired,
};

export default ExercisePreviewRow;
